// Claude: Feeds — Skip/Save deck UX, 4 modes (New/Filtered/Saved/Browsed), embedded in Search tab 14-03-2026
// Claude: preview box clickable (same as Open ↗) 01-05-2026
// Claude: N47 — standalone page at /buyer/feeds; Jumbotron + filter drawer added; asset type dropdown hidden 26-03-2026
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MdPictureAsPdf, MdTune, MdRefresh } from "react-icons/md";
import { BuyerAPI } from "../../Apis/buyerAPI";
import { AdminAPI } from "../../Apis/adminAPI";
import { Images } from "../../Assets/images";
import { Box, Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay, Flex, FormControl, FormLabel, Image, Link, Spinner, Switch, Text, VStack, useDisclosure } from "@chakra-ui/react";
import { Select, ConfigProvider } from "antd";
import { FeedsInterface, getAssetType, getEmbedUrl, LinkPreview, PdfPreview, AssetSummaryModal, openAssetOrModal } from "../../Utils/assetPreview";
import { Buyerscreens, RelevanceFieldsNewArr } from "../../Constants/data";
import { filterCountries } from "../../Utils/countries";
import actions from "../../redux/Actions";
import Jumbotron from "../../Components/Common/Jumbotron";

const COUNTRY_OPTIONS = filterCountries("").map((c) => ({ label: c.value, value: c.value }));

type FeedMode = "unread" | "filtered" | "saved" | "browsed";

const FILTERS_KEY = "aftr_buyer_search_filters";
const PAGE_SIZE = 20;


const MODE_LABEL: Record<FeedMode, string> = {
  unread: "New",
  filtered: "Filtered",
  saved: "Saved",
  browsed: "Skipped",
};

const EMPTY_MSG: Record<FeedMode, string> = {
  unread: "You're all caught up! No new content.",
  filtered: "No new content matching your filters.",
  saved: "No saved content yet. Save cards from New or Filtered view.",
  browsed: "No browsed content yet.",
};

const buildSavedFilters = () => {
  try {
    const saved = localStorage.getItem(FILTERS_KEY);
    if (saved) {
      const f = JSON.parse(saved);
      return {
        targetCountry: f.targetCountry ?? [],
        targetIndustries: f.targetIndustries ?? [],
        targetTags: f.targetTags ?? [],
        verifiedOnly: f.verifiedOnly ?? false,
      };
    }
  } catch {}
  return {};
};

const Feeds = ({ embedded = false }: { embedded?: boolean }) => {
  const dispatch = useDispatch();
  const { buyerMenu, notification: { unreadNotifications }, user } = useSelector((state: any) => state);
  const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();

  // Claude: N47 — profile + filter state for standalone mode 26-03-2026
  const [profile, setProfile] = useState<any>(null);
  const [drawerFilters, setDrawerFilters] = useState({ targetCountry: [] as string[], targetIndustries: [] as string[], targetRelevanceStatus: [] as string[] });
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [industryOptions, setIndustryOptions] = useState<{ label: string; value: string }[]>([]);
  const filtersActive = drawerFilters.targetCountry.length > 0 || drawerFilters.targetIndustries.length > 0 || drawerFilters.targetRelevanceStatus.length > 0 || verifiedOnly;

  // Claude: N47 — set active nav to Feeds when used as standalone page 26-03-2026
  useEffect(() => {
    if (!embedded) {
      dispatch(actions.buyerMenu.set({ activeMenu: Buyerscreens.FEED, aiChatOpen: buyerMenu.aiChatOpen }));
      BuyerAPI.getBuyerProfile().then((res: any) => setProfile(res?.data?.data)).catch(() => {});
      AdminAPI.getIndustryTree().then((res: any) => {
        const nodes: any[] = Array.isArray(res?.data) ? res.data : (res?.data?.data ?? []);
        setIndustryOptions(nodes.map((n: any) => ({ label: n.name, value: String(n._id) })));
      }).catch(() => {});
      const saved = localStorage.getItem("aftr_buyer_search_filters");
      if (saved) {
        try {
          const p = JSON.parse(saved);
          setDrawerFilters({ targetCountry: p.targetCountry ?? [], targetIndustries: p.targetIndustries ?? [], targetRelevanceStatus: p.targetRelevanceStatus ?? [] });
          if (p.verifiedOnly) setVerifiedOnly(p.verifiedOnly);
        } catch {}
      }
    }
  }, [embedded]);

  const handleSaveFilters = () => {
    localStorage.setItem("aftr_buyer_search_filters", JSON.stringify({ ...drawerFilters, verifiedOnly }));
    onFilterClose();
    // Re-fetch if currently in filtered mode
    setMode((m) => m === "filtered" ? "unread" : m);
    setTimeout(() => setMode("filtered"), 0);
  };

  const [mode, setMode] = useState<FeedMode>("unread");
  const [deck, setDeck] = useState<FeedsInterface[]>([]);
  const [deckIndex, setDeckIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Claude: asset summary modal state 25-03-2026
  const [summaryAsset, setSummaryAsset] = useState<FeedsInterface | null>(null);
  const fetchingRef = useRef(false);

  const fetchBatch = async (m: FeedMode, p: number, append: boolean) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (!append) setLoading(true);
    try {
      let items: FeedsInterface[] = [];

      if (m === "saved") {
        const res = await (BuyerAPI as any).getMyFeeds({ page: p, limit: PAGE_SIZE, status: "starred" });
        const d = res?.data;
        items = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
      } else if (m === "browsed") {
        const res = await (BuyerAPI as any).getMyFeeds({ page: p, limit: PAGE_SIZE, status: "read" });
        const d = res?.data;
        items = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
      } else {
        const filters = m === "filtered" ? buildSavedFilters() : {};
        const res = await (BuyerAPI as any).getUnreadFeeds({ page: p, limit: PAGE_SIZE, ...filters });
        const d = res?.data;
        items = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
      }

      setDeck(prev => append ? [...prev, ...items] : items);
      setHasMore(items.length === PAGE_SIZE);
    } catch (err) {
      console.error("Feeds fetch error:", err);
      if (!append) setDeck([]);
    } finally {
      if (!append) setLoading(false);
      fetchingRef.current = false;
    }
  };

  // Reset deck when mode changes
  useEffect(() => {
    setDeck([]);
    setDeckIndex(0);
    setPage(1);
    setHasMore(true);
    fetchBatch(mode, 1, false);
  }, [mode]);

  // Prefetch next batch when near end of deck
  useEffect(() => {
    if (!loading && hasMore && deck.length > 0 && deckIndex >= deck.length - 3 && !fetchingRef.current) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchBatch(mode, nextPage, true);
    }
  }, [deckIndex, deck.length]);

  // Claude: refresh — resets deck to page 1 for current mode 01-05-2026
  const handleRefresh = async () => {
    if (refreshing || loading) return;
    setRefreshing(true);
    setDeck([]);
    setDeckIndex(0);
    setPage(1);
    setHasMore(true);
    await fetchBatch(mode, 1, false);
    setRefreshing(false);
  };

  const handleAction = async (status: "read" | "starred") => {
    const card = deck[deckIndex];
    if (!card || actionLoading) return;
    setActionLoading(true);
    try {
      await (BuyerAPI as any).patchFeedStatus(card._id, status);
    } catch (err) {
      console.error("Feed status error:", err);
    } finally {
      setActionLoading(false);
      setDeckIndex(prev => prev + 1);
    }
  };

  const currentCard = deck[deckIndex];
  const remaining = Math.max(0, deck.length - deckIndex);
  const isDone = !loading && deckIndex >= deck.length && !hasMore;

  return (
    <Box w="100%" display="flex" flexDirection="column">
      {/* Claude: plain dark header replaces Jumbotron — Discover is now the home with Jumbotron 02-Apr-2026 */}
      {!embedded && (
        <>
          <Flex w="100%" px="30px" py={5} bg="#0E122F" color="white"
            alignItems="center" justifyContent="space-between">
            <Text fontSize="xl" fontWeight={700}>Feeds</Text>
            <Flex flexDirection="column" alignItems="center">
              <Image src={Images.AftrLogo} h="32px" objectFit="contain" />
              <Text fontSize="11px" fontWeight={300} color="whiteAlpha.600" letterSpacing="wide">buyer (beta)</Text>
            </Flex>
          </Flex>
          <Flex px={4} py={2} bg="white" borderBottom="1px solid" borderColor="gray.100" alignItems="center" gap={2}>
            <Text fontSize="lg" fontWeight={700} color="#0E122F" flexShrink={0} mr={6}>Feeds</Text>
            <Flex bg="gray.100" p="3px" borderRadius="8px" gap="2px" flexShrink={0}>
              {(["unread", "filtered", "saved", "browsed"] as FeedMode[]).map((m) => (
                <Box key={m} px={3} py="5px" borderRadius="6px" fontSize="11px" fontWeight={500}
                  cursor="pointer"
                  bg={mode === m ? "#0E122F" : "transparent"}
                  color={mode === m ? "white" : "gray.500"}
                  transition="all 0.15s"
                  _hover={{ color: mode === m ? "white" : "gray.700" }}
                  onClick={() => setMode(m)}>
                  {MODE_LABEL[m]}
                </Box>
              ))}
            </Flex>
            {/* Claude: counter + icon row pushed to right 01-05-2026 */}
            <Flex ml="auto" alignItems="center" gap={3} flexShrink={0}>
              {remaining > 1 && (
                <Text fontSize="11px" color="gray.400">{remaining} left</Text>
              )}
              {/* Claude: refresh icon — resets deck for current mode 01-05-2026 */}
              <Box cursor="pointer" onClick={handleRefresh}
                style={{ animation: refreshing ? "spin 0.6s linear infinite" : "none" }}>
                <MdRefresh size={20} color={refreshing ? "#0E122F" : "#718096"} />
              </Box>
              <Box position="relative" cursor="pointer" onClick={onFilterOpen}>
                <MdTune size={20} color={filtersActive ? "#C62828" : "#718096"} />
                {filtersActive && (
                  <Box position="absolute" top="-1px" right="-1px" w="7px" h="7px"
                    borderRadius="full" bg="#C62828" border="1.5px solid white" />
                )}
              </Box>
            </Flex>
          </Flex>
        </>
      )}

      {/* Claude: N47 — filter bar merged into Feeds header row above 26-03-2026 */}

      {/* Loading */}
      {loading && (
        <Flex justifyContent="center" alignItems="center" py={10}>
          <Spinner size="md" color="#0E122F" />
        </Flex>
      )}

      {/* Empty / done */}
      {!loading && isDone && (
        <Box px={8} py={6}>
          <Text fontSize="sm" color="gray.400">{EMPTY_MSG[mode]}</Text>
        </Box>
      )}

      {/* Card */}
      {!loading && currentCard && (
        <Box px={4} py={2} display="flex" flexDirection="column" gap={2}>
          {/* Title */}
          <Text fontSize="lg" fontWeight={700} color="primary.main" noOfLines={2}>
            {currentCard.assetTitle}
          </Text>

          {/* Preview box */}
          <Box w="100%" h="280px" bg="white" borderRadius="12px" shadow="md"
            overflow="hidden" display="flex" alignItems="center" justifyContent="center"
            cursor="pointer" onClick={() => openAssetOrModal(currentCard, setSummaryAsset)}>
            {(() => {
              const type = getAssetType(currentCard.assetUrl);
              if (type === "image") return (
                <Image src={currentCard.assetUrl} alt={currentCard.assetTitle} maxH="100%" maxW="100%" objectFit="contain" p={2} />
              );
              if (type === "video") return (
                <Box w="100%" h="100%">
                  <iframe src={getEmbedUrl(currentCard.assetUrl)} title={currentCard.assetTitle}
                    width="100%" height="100%" style={{ border: "none" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen />
                </Box>
              );
              if (type === "pdf") return currentCard.assetSummary
                ? <PdfPreview summary={currentCard.assetSummary} url={currentCard.assetUrl} />
                : <Flex direction="column" alignItems="center" justifyContent="center" w="100%" h="100%" p={6}>
                    <MdPictureAsPdf size={80} color="#E53E3E" />
                    <Text mt={3} fontSize="lg" fontWeight={600} color="gray.600" textTransform="uppercase">PDF</Text>
                  </Flex>;
              return <LinkPreview url={currentCard.assetUrl} title={currentCard.assetTitle} coverImage={currentCard.coverImage} />;
            })()}
          </Box>

          {/* Actions */}
          <Flex gap={3}>
            {/* Skip: available in New, Filtered, Saved modes */}
            {(mode === "unread" || mode === "filtered" || mode === "saved") && (
              <Button flex={1} variant="outline" borderColor="gray.300" color="gray.500"
                borderRadius="10px" h="48px" fontSize="sm" fontWeight={600}
                isLoading={actionLoading}
                onClick={() => handleAction("read")}
                _hover={{ bg: "gray.50" }}>
                Skip
              </Button>
            )}
            {/* Save: available in New, Filtered, Browsed modes */}
            {(mode === "unread" || mode === "filtered" || mode === "browsed") && (
              <Button flex={1} bg="#0E122F" color="white"
                borderRadius="10px" h="48px" fontSize="sm" fontWeight={600}
                isLoading={actionLoading}
                onClick={() => handleAction("starred")}
                _hover={{ bg: "#1a2040" }}>
                Save
              </Button>
            )}
          </Flex>

          {/* Claude: open asset — show summary modal if summary exists, else open URL directly 25-03-2026 */}
          {/* Previously: <Link href={currentCard.assetUrl} isExternal fontSize="xs" color="blue.400">Open ↗</Link> */}
          <Text
            fontSize="xs" color="blue.400" textAlign="center" cursor="pointer"
            onClick={() => openAssetOrModal(currentCard, setSummaryAsset)}
          >
            Open ↗
          </Text>
        </Box>
      )}

      {/* Claude: asset summary modal 25-03-2026 */}
      <AssetSummaryModal
        isOpen={!!summaryAsset}
        onClose={() => setSummaryAsset(null)}
        asset={summaryAsset}
      />

      {/* Claude: N47 — filter drawer for standalone Feeds page 26-03-2026 */}
      {!embedded && (
        <Drawer isOpen={isFilterOpen} placement="right" onClose={onFilterClose}>
          <DrawerOverlay />
          <DrawerContent maxW="320px">
            <DrawerCloseButton />
            <DrawerHeader fontSize="md">Search Filters</DrawerHeader>
            <DrawerBody>
              <ConfigProvider theme={{ token: { zIndexPopupBase: 1500 } }}>
                <VStack spacing={3} alignItems="stretch">
                  <Box>
                    <Text fontSize="11px" color="gray.500" mb={1}>Country</Text>
                    <Select mode="multiple" showSearch style={{ width: "100%" }}
                      placeholder="Select countries" options={COUNTRY_OPTIONS}
                      filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                      value={drawerFilters.targetCountry}
                      onChange={(v: string[]) => setDrawerFilters((f) => ({ ...f, targetCountry: v }))} />
                  </Box>
                  <Box>
                    <Text fontSize="11px" color="gray.500" mb={1}>Industry</Text>
                    <Select mode="multiple" showSearch style={{ width: "100%" }}
                      placeholder="Select industries" options={industryOptions}
                      filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                      value={drawerFilters.targetIndustries}
                      onChange={(v: string[]) => setDrawerFilters((f) => ({ ...f, targetIndustries: v }))} />
                  </Box>
                  <Box>
                    <Text fontSize="11px" color="gray.500" mb={1}>Relevance Status</Text>
                    <Select mode="multiple" style={{ width: "100%" }} placeholder="Select statuses"
                      value={drawerFilters.targetRelevanceStatus}
                      onChange={(v: string[]) => setDrawerFilters((f) => ({ ...f, targetRelevanceStatus: v }))}
                      options={RelevanceFieldsNewArr.map((r) => ({
                        label: (
                          <Flex alignItems="center" gap="6px">
                            <Box w="12px" h="12px" borderRadius="2px" bg={r.colorHash} flexShrink={0} />
                            <Text fontSize="12px">{r.title}</Text>
                          </Flex>
                        ),
                        value: r.status,
                      }))} />
                  </Box>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel fontSize="12px" color="gray.600" mb={0} mr={2}>Verified Sellers Only</FormLabel>
                    <Switch colorScheme="purple" isChecked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} />
                  </FormControl>
                  <Button size="sm" variant="outline" borderColor="#1565C0" color="#1565C0"
                    borderRadius="8px" fontSize="12px" px={3} h="28px" _hover={{ bg: "#E3F2FD" }}
                    onClick={handleSaveFilters}>
                    Save Filters
                  </Button>
                </VStack>
              </ConfigProvider>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </Box>
  );
};

export default Feeds;
